import { useEffect, useRef } from 'react';
import useAudioStore from '../../stores/useAudioStore';
import useTimerStore from '../../stores/useTimerStore';
import useFlowStore from '../../stores/useFlowStore';

// === Audio Generators ===
function createBrownNoiseBuffer(ctx) {
    const bufferSize = 2 * ctx.sampleRate; // 2 sec loop
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let lastOut = 0;
    for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2 - 1;
        data[i] = (lastOut + 0.02 * white) / 1.02;
        lastOut = data[i];
        data[i] *= 3.5; // Gain compensation
    }
    return buffer;
}

function createPinkNoiseBuffer(ctx) {
    const bufferSize = 2 * ctx.sampleRate;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2 - 1;
        b0 = 0.99886 * b0 + white * 0.0555179;
        b1 = 0.99332 * b1 + white * 0.0750759;
        b2 = 0.96900 * b2 + white * 0.1538520;
        b3 = 0.86650 * b3 + white * 0.3104856;
        b4 = 0.55000 * b4 + white * 0.5329522;
        b5 = -0.7616 * b5 - white * 0.0168980;
        data[i] = b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362;
        data[i] *= 0.11;
        b6 = white * 0.115926;
    }
    return buffer;
}

function createWhiteNoiseBuffer(ctx) {
    const bufferSize = 2 * ctx.sampleRate;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
        data[i] = (Math.random() * 2 - 1) * 0.5;
    }
    return buffer;
}

// === Configuration ===
const PHASE_CONFIG = {
    freestyle: {
        prep: { lfoFreq: 0, depthTarget: 0, spatial: 'forward' },
        focus: { lfoFreq: 16, depthTarget: 1, spatial: 'forward' },
        reflect: { lfoFreq: 6, depthTarget: 0.5, spatial: 'sweep' },
        rest: { lfoFreq: 8, depthTarget: 0.5, spatial: 'sweep' },
    }
};

const EFFECT_LEVELS = {
    low: 0.10,
    medium: 0.25,
    high: 0.45,
};

export default function AudioEngine() {
    const audioState = useAudioStore();
    const timerState = useTimerStore();
    const bpState = useFlowStore();

    // The audio context itself
    const ctxRef = useRef(null);
    
    // Nodes that exist for the lifetime of the session
    const nodesRef = useRef(null);
    
    // Current state tracking to avoid redundant updates
    const stateRef = useRef({
        isActive: false,
        soundscapeType: null,
    });

    const getActivePhase = () => {
        if (bpState.executionState === 'running') return bpState.currentPhase;
        if (timerState.isRunning) return timerState.phase;
        return null;
    };

    // User interaction unlocks Web Audio API
    useEffect(() => {
        const initAudio = () => {
            if (!ctxRef.current) {
                const AudioContext = window.AudioContext || window.webkitAudioContext;
                ctxRef.current = new AudioContext();
            }
            if (ctxRef.current.state === 'suspended') {
                ctxRef.current.resume();
            }
        };

        window.addEventListener('click', initAudio, { once: true });
        window.addEventListener('keydown', initAudio, { once: true });
        return () => {
            window.removeEventListener('click', initAudio);
            window.removeEventListener('keydown', initAudio);
        };
    }, []);

    // Main Engine Orchestration Effect
    useEffect(() => {
        const phase = getActivePhase();
        const isActiveSession = audioState.enabled && phase !== null;
        const config = PHASE_CONFIG.freestyle[phase] || PHASE_CONFIG.freestyle.prep;

        // --- TEARDOWN / STOP ---
        if (!isActiveSession || !ctxRef.current) {
            if (stateRef.current.isActive && nodesRef.current) {
                const { masterGain } = nodesRef.current;
                if (ctxRef.current.state !== 'closed') {
                    masterGain.gain.setTargetAtTime(0, ctxRef.current.currentTime, 0.5);
                }
                stateRef.current.isActive = false;
            }
            return;
        }

        const ctx = ctxRef.current;
        const now = ctx.currentTime;

        // --- SETUP NODES (Run Once) ---
        if (!nodesRef.current) {
            // Signal Chain: Source -> ModGain -> MasterGain -> Destination
            // LFO Chain: Primary LFO -> LFODepthGain -> ModGain.gain
            // ADHD Chain: ADHD LFO -> AdhdGain -> ModGain.gain (varies modulation depth over time)
            
            const masterGain = ctx.createGain();
            masterGain.gain.value = 0; // Start silenced
            masterGain.connect(ctx.destination);

            // HRTF Panner
            const panner = ctx.createPanner();
            panner.panningModel = 'HRTF';
            panner.distanceModel = 'inverse';
            panner.refDistance = 1;
            panner.maxDistance = 10000;
            panner.positionX.value = 0;
            panner.positionZ.value = -1; // Forward by default
            panner.connect(masterGain);

            const modGain = ctx.createGain();
            modGain.gain.value = 1;
            modGain.connect(panner);

            // Primary LFO (Amplitude Modulation)
            const lfo = ctx.createOscillator();
            lfo.type = 'sine';
            lfo.frequency.value = 0;
            lfo.start(0);

            const lfoDepthGain = ctx.createGain();
            lfoDepthGain.gain.value = 0;
            lfo.connect(lfoDepthGain);
            lfoDepthGain.connect(modGain.gain);

            // ADHD dynamic variation LFO
            const adhdLfo = ctx.createOscillator();
            adhdLfo.type = 'sine';
            adhdLfo.frequency.value = 0.03; // ~33s cycle
            adhdLfo.start(0);

            const adhdGain = ctx.createGain();
            adhdGain.gain.value = 0;
            adhdLfo.connect(adhdGain);
            adhdGain.connect(modGain.gain);

            nodesRef.current = {
                source: null,
                masterGain,
                panner,
                modGain,
                lfo,
                lfoDepthGain,
                adhdLfo,
                adhdGain
            };
        }

        const nodes = nodesRef.current;

        // --- HANDLE SOUNDSCAPE SOURCE SWAPPING ---
        if (!nodes.source || stateRef.current.soundscapeType !== audioState.soundscape) {
            if (nodes.source) {
                nodes.source.stop(now + 1); // Subtly Crossfade
            }

            const source = ctx.createBufferSource();
            let buf;
            if (audioState.soundscape === 'pink-noise') buf = createPinkNoiseBuffer(ctx);
            else if (audioState.soundscape === 'white-noise') buf = createWhiteNoiseBuffer(ctx);
            else buf = createBrownNoiseBuffer(ctx);

            source.buffer = buf;
            source.loop = true;
            source.connect(nodes.modGain);
            source.start(now);
            nodes.source = source;
            stateRef.current.soundscapeType = audioState.soundscape;
        }

        // --- APPLY MASTER VOLUME ---
        if (!stateRef.current.isActive) {
            nodes.masterGain.gain.setTargetAtTime(audioState.volume, now, 1.0); // Slow fade in
            stateRef.current.isActive = true;
        } else {
            nodes.masterGain.gain.setTargetAtTime(audioState.volume, now, 0.1); // Quick adjust
        }

        // --- APPLY PHASE-REACTIVE MODULATION ---
        const depthBase = EFFECT_LEVELS[audioState.effectLevel] || EFFECT_LEVELS.medium;
        const targetDepth = config.depthTarget * depthBase;

        // Base LFO Frequency (e.g., 16Hz for beta)
        if (config.lfoFreq > 0) {
            nodes.lfo.frequency.setTargetAtTime(config.lfoFreq, now, 1.0);
        }

        // Modulation Depth (LFO Amplitude)
        nodes.lfoDepthGain.gain.setTargetAtTime(targetDepth, now, 1.5);

        // ADHD Mode (Dynamic Depth Variation)
        if (audioState.adhdMode && config.lfoFreq > 0) {
            if (phase === 'focus') {
                 nodes.lfo.frequency.setTargetAtTime(16, now, 1.0); // Lock to 16Hz
            }
            nodes.adhdGain.gain.setTargetAtTime(0.15, now, 1.0); // Add ±0.15 variation
        } else {
            nodes.adhdGain.gain.setTargetAtTime(0, now, 1.0); // Disable variation
        }

        // --- HRTF SPATIAL POSITIONING ---
        if (audioState.spatialAudio) {
            nodes.panner.panningModel = 'HRTF';
            if (config.spatial === 'forward') {
                nodes.panner.positionX.setTargetAtTime(0, now, 1.0);
                nodes.panner.positionZ.setTargetAtTime(-1, now, 1.0);
            } else if (config.spatial === 'sweep') {
                // Static side for MVP, proper orbit requires custom JS loop
                nodes.panner.positionX.setTargetAtTime(0.5, now, 2.0); 
                nodes.panner.positionZ.setTargetAtTime(-0.5, now, 2.0);
            }
        } else {
            // Disable Spatial
            nodes.panner.panningModel = 'equalpower';
            nodes.panner.positionX.setTargetAtTime(0, now, 0.1);
            nodes.panner.positionZ.setTargetAtTime(0, now, 0.1);
        }

    }, [
        audioState.enabled, 
        audioState.volume, 
        audioState.soundscape, 
        audioState.effectLevel, 
        audioState.adhdMode, 
        audioState.spatialAudio,
        timerState.isRunning, 
        timerState.phase,
        bpState.executionState,
        bpState.currentPhase
    ]);

    // Deep cleanup on global unmount
    useEffect(() => {
        return () => {
            if (ctxRef.current && nodesRef.current?.masterGain) {
                const now = ctxRef.current.currentTime;
                nodesRef.current.masterGain.gain.setTargetAtTime(0, now, 0.2);
                setTimeout(() => {
                    if (ctxRef.current.state !== 'closed') {
                        ctxRef.current.close().catch(() => {});
                    }
                }, 1000);
            }
        };
    }, []);

    return null;
}
